import React, { useEffect } from "react";
import { BiTrash } from "react-icons/bi";
import "./filter.css";

/**
 * FilterItem Component
 * 
 * Renders a single filter condition item with field, operator, and value selectors.
 * Automatically updates operator options based on field data type (text uses text operators,
 * numbers use numeric operators, booleans use boolean operators, etc).
 * Receives condition data from parent array and updates it via callback.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.fields - Available fields for filtering
 * @param {string} props.connective - AND/OR logic for this condition
 * @param {Function} props.setConnective - Setter for connective
 * @param {number} props.id - Condition ID in array
 * @param {Array} props.array - Conditions array from parent
 * @param {Function} props.remove - Remove condition callback
 * @param {Function} props.update - Update condition callback
 * @returns {JSX.Element} Filter condition row with selectors
 */

const operators_text = [
  "contains",
  "does not contain",
  "is",
  "is not",
  "is empty",
  "is not empty",
];

const operators_isActive = ["is", "is not", "is empty", "is not empty"];

const operators_number = [
  "is equal to",
  "is not equal to",
  "is greater than",
  "is less than",
  "is greater than or equal to",
  "is less than or equal to",
  "is empty",
  "is not empty",
];

const rounds = [
  "undisclosed",
  "seed round",
  "series A",
  "series B",
  "grant",
  "private equity round",
  "funding round",
  "angel round",
  "debt financing",
  "product crowdfunding",
];

const value_isActive = ["active", "inactive"];

// function to check the data type for the corresponding field name
const getDataType = (fieldName) => {
  console.log("fieldName:", fieldName);
  if (
    fieldName.substr(0, 3).toLowerCase() === "num" ||
    fieldName.toLowerCase().includes("total") ||
    fieldName.toLowerCase() === "revenue" ||
    fieldName.toLowerCase() === "operationalduration"
  ) {
    return "number";
  }

  if (fieldName.toLowerCase().includes("date")) {
    return "date";
  }

  if (fieldName.includes("isActive")) {
    return "boolean";
  }

  return "text";
};

const FilterItem = ({
  fields,
  connective,
  setConnective,
  id,
  array,
  remove,
  update,
}) => {
  // useEffect everytime the overall array changes length
  useEffect(() => {
    // console.log('value:', array[id].value)
    if (id !== 0) {
      update(id, { ...array[id], connective: connective });
    }
  }, [array.length]);

  // useEffect everytime the connective changes
  useEffect(() => {
    update(id, { ...array[id], connective: connective });
  }, [connective]);

  // useEffect everytime the selected field changes, which updates the dtype for the condition obj array
  useEffect(() => {
    if (getDataType(array[id].field) === "number") {
      update(id, {
        ...array[id],
        operator: operators_number[0],
        dtype: "number",
        value: "",
      });
    }

    if (getDataType(array[id].field) === "boolean") {
      update(id, {
        ...array[id],
        operator: operators_isActive[0],
        dtype: "boolean",
        value: "",
      });
    }

    if (getDataType(array[id].field) === "date") {
      update(id, { ...array[id], dtype: "date", value: "" });
    }

    if (getDataType(array[id].field) === "text") {
      update(id, { ...array[id], dtype: "text", value: "" });
    }
  }, [array[id].field]);

  // useEffect everytime operator "is empty" / "is not empty" is choosen
  useEffect(() => {
    update(id, { ...array[id], dtype: "text", value: "" });
  }, [
    array[id].operator.toLowerCase() === "is empty" ||
    array[id].operator.toLowerCase() === "is not empty",
  ]);

  return (
    <div className="flex justify-start" style={{ margin: "0.65rem 0rem" }}>
      <div
        className="w-300"
        style={{
          marginRight: "1rem",
          textAlign: "right",
        }}
      >
        {id === 0 ? (
          <>Where</>
        ) : id === 1 ? (
          <select
            className="form-cell"
            value={connective}
            onChange={(e) => {
              update(id, { ...array[id], connective: e.target.value });
              setConnective(e.target.value);
            }}
          >
            <option className="option" value="and">
              and
            </option>
            <option className="option" value="or">
              or
            </option>
          </select>
        ) : (
          connective
        )}
      </div>
      <div className="flex w-full">
        {/* Field selection */}
        <select
          className="form-cell select operand"
          onChange={(e) => {
            update(id, { ...array[id], field: e.target.value });
          }}
          value={array[id].field}
        >
          {fields
            .filter((field) => field.actualName !== "description") // filter out description
            .map((field, index) => (
              <option key={index} className="option" value={field.actualName}>
                {field.displayName}
              </option>
            ))}
        </select>

        {/* Operator selection */}
        {getDataType(array[id].field) === "number" ? (
          <select
            className="form-cell select operator"
            onChange={(e) => {
              update(id, { ...array[id], operator: e.target.value });
            }}
            value={array[id].operator}
          >
            {operators_number.map((operator, index) => (
              <option key={index} value={operator}>
                {operator}
              </option>
            ))}
          </select>
        ) : getDataType(array[id].field) === "boolean" ? (
          <select
            className="form-cell select operator"
            onChange={(e) => {
              update(id, { ...array[id], operator: e.target.value });
            }}
            value={array[id].operator}
          >
            {operators_isActive.map((operator, index) => (
              <option key={index} value={operator}>
                {operator}
              </option>
            ))}
          </select>
        ) : (
          <select
            className="form-cell select operator"
            onChange={(e) => {
              update(id, { ...array[id], operator: e.target.value });
            }}
            value={array[id].operator}
          >
            {operators_text.map((operator, index) => (
              <option key={index} value={operator}>
                {operator}
              </option>
            ))}
          </select>
        )}

        {/* value input or selection */}
        {array[id].operator.toLowerCase() === "is empty" ||
          array[id].operator.toLowerCase() === "is not empty" ? (
          <input
            className="form-cell input value"
            style={{ background: "white" }}
            type="text"
            value=""
            disabled
          />
        ) : array[id].field.toLowerCase().includes("round") &&
          !array[id].field.substr(0, 3).toLowerCase() === "num" ? (
          <select
            className="form-cell select operator"
            onChange={(e) =>
              update(id, { ...array[id], value: e.target.value })
            }
            value={array[id].value || ""}
          >
            <option value="">--Please select an option--</option>
            {rounds.sort().map((round, index) => (
              <option key={index} value={round}>
                {round}
              </option>
            ))}
          </select>
        ) : getDataType(array[id].field) === "boolean" ? (
          <select
            className="form-cell select operator"
            onChange={(e) =>
              update(id, { ...array[id], value: e.target.value })
            }
            value={array[id].value || ""}
          >
            <option value="">--Please select an option--</option>
            {value_isActive.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="form-cell input value"
            name="name"
            value={array[id].value}
            type={
              getDataType(array[id].field) === "number"
                ? "number"
                : getDataType(array[id].field) === "date"
                  ? "date"
                  : "text"
            }
            placeholder={
              getDataType(array[id].field) === "number"
                ? "Enter a number"
                : "Enter a value"
            }
            onChange={(e) => {
              update(id, { ...array[id], value: e.target.value });
            }}
          />
        )}

        <div
          className="form-cell condition-icon pointer"
          onClick={() => remove(id)}
        >
          <BiTrash />
        </div>
      </div>
    </div>
  );
};

export default FilterItem;
